import BoardSlider from "../../components/slider/BoardSlider";
import GameLifeline from "./GameLifeline";
import { useParams, useNavigate, useBlocker } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useLifelineAPI, endGameAPI } from "../../api/game";
import Loader from "../../components/Loader/Loader";
import { useEffect, useState, useRef } from "react";
import LifelineConfirmModal from "../../components/Modal/LifelineConfirmModal";
import LifelineSuccessModal from "../../components/Modal/LifelineSuccessModal";
import Ra7El7lalModal from "../../components/Modal/Ra7El7lalModal";
import SpinStealModal from "../../components/Modal/SpinStealModal";
import SwitchCategoryModal from "../../components/Modal/SwitchCategoryModal";
import useToast from "../../hooks/useToast";
import { useTranslation } from "react-i18next";
import GamePlayImg from "../../assets/images/game-board/game-play-2.png";
import { useGameDetailsFromLayout } from "../../layouts/Game/GameDetailsContext";

// Lifeline interface for type safety
interface Lifeline {
  lifeline_id: {
    name: { en: string; ar: string };
    description: { en: string; ar: string };
    image: string;
    _id: string;
    slug: string;
  };
  is_used: boolean;
  _id: string;
}

interface UseLifelinePayload {
  lifeline_id: string | null;
  team_lifeline_id?: string | null;
  game_id?: string | null;
  team_id?: string | null;
  opponent_team_id?: string | null;
  opponent_team_lifeline_id?: string | null;
  points?: number | null;
  category_id?: string | null;
}

const GameBoard = () => {
  const { gameId } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Lifeline modal state
  const [showLifelineModal, setShowLifelineModal] = useState(false);
  const [selectedLifeline, setSelectedLifeline] = useState<Lifeline | null>(null);
  const [selectedLifelineTeamId, setSelectedLifelineTeamId] = useState<string>("");
  const [selectedLifelineTeamName, setSelectedLifelineTeamName] = useState<string>("");
  const [isUsingLifeline, setIsUsingLifeline] = useState(false);

  // Lifeline success modal state
  const [showLifelineSuccessModal, setShowLifelineSuccessModal] = useState(false);
  const [usedLifeline, setUsedLifeline] = useState<Lifeline | null>(null);
  const [usedLifelineTeamName, setUsedLifelineTeamName] = useState<string>("");
  const [lifelineSuccessMessage, setLifelineSuccessMessage] = useState<string>("");

  // Ra7 El 7lal lifeline state
  const [showRa7El7lalModal, setShowRa7El7lalModal] = useState(false);
  const [isRa7El7lalLoading, setIsRa7El7lalLoading] = useState(false);
  const ra7El7lalTeamIdRef = useRef<string>("");
  const ra7El7lalTeamNameRef = useRef<string>("");
  const ra7El7lalLifelineRef = useRef<Lifeline | null>(null);

  // Nuke lifeline state
  const [showNukeTeamModal, setShowNukeTeamModal] = useState(false);
  const nukeTeamIdRef = useRef<string>("");
  const nukeTeamNameRef = useRef<string>("");
  const nukeLifelineIdRef = useRef<string>("");
  const nukeTeamLifelineIdRef = useRef<string>("");

  // Spin Steal lifeline state
  const [showSpinStealModal, setShowSpinStealModal] = useState(false);
  const [isSpinStealLoading, setIsSpinStealLoading] = useState(false);
  const spinStealTeamIdRef = useRef<string>("");
  const spinStealTeamNameRef = useRef<string>("");
  const spinStealLifelineRef = useRef<Lifeline | null>(null);

  // Switch Category lifeline state
  const [showSwitchCategoryModal, setShowSwitchCategoryModal] = useState(false);
  const [isSwitchCategoryLoading, setIsSwitchCategoryLoading] = useState(false);
  const switchTeamIdRef = useRef<string>("");
  const switchTeamNameRef = useRef<string>("");
  const switchLifelineRef = useRef<Lifeline | null>(null);

  // Prevent browser back from leaving the game board (works with React Router history)
  const backBlocker = useBlocker(({ historyAction }) => historyAction === "POP");

  useEffect(() => {
    if (backBlocker.state === "blocked") {
      backBlocker.reset();
    }
  }, [backBlocker, backBlocker.state]);

  const gameDetailsFromLayout = useGameDetailsFromLayout();
  const gameData = (gameDetailsFromLayout?.game ?? null) as any;
  const teamData = gameData?.teams || [];
  const current_team_turn = gameData?.current_team_turn;

  // Use lifeline mutation
  const { mutate: useLifeline } = useMutation({
    mutationFn: useLifelineAPI,
    onSuccess: (data) => {
      useToast(data?.message || "Lifeline used successfully", "success");
      queryClient.invalidateQueries({ queryKey: ["gameDetails", gameId] });
    },
    onError: (error: any) => {
      useToast(error.message || "Failed to use lifeline", "error");
      setIsUsingLifeline(false);
    },
  });

  // End game mutation for automatic game completion
  const { mutate: endGame } = useMutation({
    mutationFn: endGameAPI,
    onSuccess: (data) => {
      navigate(`/result/${data?.data?.game_id}`, { replace: true });
    },
    onError: (error: any) => {
      console.error("Failed to end game automatically:", error);
      useToast(error.message || "Failed to end game", "error");
    },
  });

  // Check if all questions are answered and automatically end the game
  useEffect(() => {
    if (!gameData || !gameData.categories) {
      return;
    }

    // Count total questions and answered questions across all categories
    let totalQuestions = 0;
    let answeredQuestions = 0;

    gameData.categories.forEach((category: any) => {
      if (category.questions && Array.isArray(category.questions)) {
        category.questions.forEach((question: any) => {
          totalQuestions++;
          // A question is considered answered if it has answered_by OR is_timeout
          if (question.answered_by !== null || question.is_timeout === true) {
            answeredQuestions++;
          }
        });
      }
    });

    // If all questions are answered, automatically end the game
    if (totalQuestions > 0 && answeredQuestions === totalQuestions) {
      endGame({ game_id: gameId as string });
      useToast(t("gameBoard.gameEndedAutomatically"), "success");
    }
  }, [gameData, gameId, endGame]);

  // Handle lifeline click - check if it's Ra7 El 7lal or Nuke
  const handleLifelineClick = (lifeline: Lifeline, teamId: string, teamName: string) => {
    // Check if it's the team's turn
    if (current_team_turn?._id !== teamId) {
      useToast(t("gameBoard.notYourTurn") || "It's not your turn to use lifelines", "error");
      return;
    }
    const lifelineName = lifeline.lifeline_id?.slug;

    // Only handle specific lifelines on the game board
    if (
      lifelineName === "ra7-el-7lal" ||
      lifelineName === "nuke" ||
      lifelineName === "spin-steal" ||
      lifelineName === "switch"
    ) {
      setSelectedLifeline(lifeline);
      setSelectedLifelineTeamId(teamId);
      setSelectedLifelineTeamName(teamName);
      setShowLifelineModal(true);
      return;
    }
    // Other lifelines are not available on the game board
    useToast(t("gameBoard.lifelineNotAvailableOnGameBoard"), "info");
    return;
  };

  // Handle lifeline confirmation
  const handleLifelineConfirm = () => {
    if (!gameId || !selectedLifeline || !selectedLifelineTeamId) return;

    const lifelineSlug = selectedLifeline.lifeline_id?.slug || "";
    setIsUsingLifeline(true);

    if (lifelineSlug === "ra7-el-7lal") {
      // Store the team info in refs
      ra7El7lalTeamIdRef.current = selectedLifelineTeamId;
      ra7El7lalTeamNameRef.current = selectedLifelineTeamName;
      ra7El7lalLifelineRef.current = selectedLifeline;
      // Close the confirm modal and open Ra7 El 7lal modal
      setShowLifelineModal(false);
      setIsUsingLifeline(false);
      setShowRa7El7lalModal(true);
    } else if (lifelineSlug === "nuke") {
      // Store the team info in refs
      nukeTeamIdRef.current = selectedLifelineTeamId;
      nukeTeamNameRef.current = selectedLifelineTeamName;
      nukeLifelineIdRef.current = selectedLifeline.lifeline_id._id;
      nukeTeamLifelineIdRef.current = selectedLifeline._id;

      // Get opponent teams
      const opponentTeams = teamData?.filter((team: any) => team._id !== selectedLifelineTeamId) || [];

      // If only 1 opponent, auto-select and call API directly
      if (opponentTeams.length === 1) {
        const opponent = opponentTeams[0];
        const nukePayload: UseLifelinePayload = {
          lifeline_id: selectedLifeline.lifeline_id._id,
          team_lifeline_id: selectedLifeline._id,
          game_id: gameId,
          team_id: selectedLifelineTeamId,
          opponent_team_id: opponent._id,
        };
        useLifeline(nukePayload, {
          onSuccess: (data) => {
            setUsedLifeline(selectedLifeline);
            setUsedLifelineTeamName(selectedLifelineTeamName);
            setLifelineSuccessMessage(t("nuke.nukeActivatedSuccessfully", { opponentTeamName: opponent.team_name }));
            setShowLifelineModal(false);
            setIsUsingLifeline(false);
            setShowLifelineSuccessModal(true);
          },
          onError: () => {
            setIsUsingLifeline(false);
          },
        });
      } else {
        // Multiple opponents - show team selection modal
        setShowLifelineModal(false);
        setIsUsingLifeline(false);
        setShowNukeTeamModal(true);
      }
    } else if (lifelineSlug === "spin-steal") {
      // Store the team info in refs
      spinStealTeamIdRef.current = selectedLifelineTeamId;
      spinStealTeamNameRef.current = selectedLifelineTeamName;
      spinStealLifelineRef.current = selectedLifeline;
      // Close the confirm modal and open Spin Steal modal
      setShowLifelineModal(false);
      setIsUsingLifeline(false);
      setShowSpinStealModal(true);
    } else if (lifelineSlug === "switch") {
      // Store the team info in refs
      switchTeamIdRef.current = selectedLifelineTeamId;
      switchTeamNameRef.current = selectedLifelineTeamName;
      switchLifelineRef.current = selectedLifeline;

      // Close the confirm modal and open Switch Category modal
      setShowLifelineModal(false);
      setIsUsingLifeline(false);
      setShowSwitchCategoryModal(true);
    }
  };

  // Handle Ra7 El 7lal modal confirm
  const handleRa7El7lalConfirm = (opponentTeamId: string | null, points: number | null) => {
    if (!gameId || !ra7El7lalLifelineRef.current) return;

    setIsRa7El7lalLoading(true);

    const payload: UseLifelinePayload = {
      lifeline_id: ra7El7lalLifelineRef.current.lifeline_id._id,
      team_lifeline_id: ra7El7lalLifelineRef.current._id,
      game_id: gameId,
      team_id: ra7El7lalTeamIdRef.current,
    };

    // If opponent team is selected, always deduct 500 points
    if (opponentTeamId) {
      payload.opponent_team_id = opponentTeamId;
      payload.points = 500;
    }

    const getSuccessMessage = () => {
      if (opponentTeamId) {
        const opponentTeam = teamData?.find((t: any) => t._id === opponentTeamId);
        const opponentName = opponentTeam?.team_name || "opponent";
        return t("ra7El7lal.pointsDeducted", { points: 500, opponentName: opponentName });
      }
      return t("ra7El7lal.lifelineUsedButNoPointsDeducted");
    };

    useLifeline(payload, {
      onSuccess: () => {
        setLifelineSuccessMessage(getSuccessMessage());
        setUsedLifeline(ra7El7lalLifelineRef.current);
        setUsedLifelineTeamName(ra7El7lalTeamNameRef.current);
        setShowRa7El7lalModal(false);
        setIsRa7El7lalLoading(false);
        setShowLifelineSuccessModal(true);
      },
      onError: () => {
        setIsRa7El7lalLoading(false);
      },
    });
  };

  // Handle Ra7 El 7lal modal close
  const handleRa7El7lalModalClose = () => {
    setShowRa7El7lalModal(false);
    ra7El7lalTeamIdRef.current = "";
    ra7El7lalTeamNameRef.current = "";
    ra7El7lalLifelineRef.current = null;
  };

  // Handle Nuke team selection
  const handleNukeTeamSelect = (opponentTeamId: string, opponentTeamName: string) => {
    const nukePayload: UseLifelinePayload = {
      lifeline_id: nukeLifelineIdRef.current,
      team_lifeline_id: nukeTeamLifelineIdRef.current,
      game_id: gameId,
      team_id: nukeTeamIdRef.current,
      opponent_team_id: opponentTeamId,
    };

    useLifeline(nukePayload, {
      onSuccess: () => {
        setUsedLifeline(selectedLifeline);
        setUsedLifelineTeamName(nukeTeamNameRef.current);
        setLifelineSuccessMessage(t("nuke.nukeActivatedSuccessfully", { opponentTeamName: opponentTeamName }));
        setShowNukeTeamModal(false);
        setShowLifelineSuccessModal(true);
      },
    });
  };

  // Handle Nuke team modal close
  const handleNukeTeamModalClose = () => {
    setShowNukeTeamModal(false);
    nukeTeamIdRef.current = "";
    nukeTeamNameRef.current = "";
    nukeLifelineIdRef.current = "";
    nukeTeamLifelineIdRef.current = "";
  };

  // Handle Spin Steal modal confirm
  const handleSpinStealConfirm = (opponentTeamId: string, opponentTeamLifelineInstanceId: string) => {
    if (!gameId || !spinStealLifelineRef.current) return;

    const opponentTeam = teamData?.find((t: any) => t._id === opponentTeamId);
    const stolenLifeline = opponentTeam?.lifelines?.find((l: Lifeline) => l._id === opponentTeamLifelineInstanceId);
    if (!stolenLifeline) return;

    setIsSpinStealLoading(true);

    const payload: UseLifelinePayload = {
      lifeline_id: spinStealLifelineRef.current.lifeline_id._id,
      team_lifeline_id: spinStealLifelineRef.current._id,
      game_id: gameId,
      team_id: spinStealTeamIdRef.current,
      opponent_team_id: opponentTeamId,
      opponent_team_lifeline_id: opponentTeamLifelineInstanceId,
    };

    const opponentTeamName = opponentTeam?.team_name || "opponent";
    const stolenLifelineName =
      stolenLifeline?.lifeline_id?.name?.[t("language") === "ar" ? "ar" : "en"] ||
      stolenLifeline?.lifeline_id?.name?.en ||
      "lifeline";

    useLifeline(payload, {
      onSuccess: () => {
        setLifelineSuccessMessage(
          t("spinSteal.lifelineStolenSuccessfully", {
            lifelineName: stolenLifelineName,
            opponentTeamName: opponentTeamName,
          }) || `Successfully stole ${stolenLifelineName} from ${opponentTeamName}`
        );
        setUsedLifeline(spinStealLifelineRef.current);
        setUsedLifelineTeamName(spinStealTeamNameRef.current);
        setShowSpinStealModal(false);
        setIsSpinStealLoading(false);
        setShowLifelineSuccessModal(true);
      },
      onError: () => {
        setIsSpinStealLoading(false);
      },
    });
  };

  // Handle Spin Steal modal close
  const handleSpinStealModalClose = () => {
    setShowSpinStealModal(false);
    spinStealTeamIdRef.current = "";
    spinStealTeamNameRef.current = "";
    spinStealLifelineRef.current = null;
  };

  // Handle Switch Category modal confirm
  const handleSwitchCategoryConfirm = (selectedCategoryId: string) => {
    if (!gameId || !switchLifelineRef.current || !selectedCategoryId) return;

    setIsSwitchCategoryLoading(true);

    const payload: UseLifelinePayload = {
      lifeline_id: switchLifelineRef.current.lifeline_id._id,
      team_lifeline_id: switchLifelineRef.current._id,
      game_id: gameId,
      team_id: switchTeamIdRef.current,
      category_id: selectedCategoryId,
    };

    const selectedCategory = gameData?.categories?.find((c: any) => c._id === selectedCategoryId);
    const currentLanguage = t("language") === "ar" ? "ar" : "en";
    const categoryName =
      selectedCategory?.category_id?.name?.[currentLanguage] ||
      selectedCategory?.category_id?.name?.en ||
      t("switchCategory.category") ||
      "category";

    useLifeline(payload, {
      onSuccess: () => {
        setLifelineSuccessMessage(
          t("switchCategory.categorySwitchedSuccessfully", { categoryName }) || `Category switched to ${categoryName}`
        );
        setUsedLifeline(switchLifelineRef.current);
        setUsedLifelineTeamName(switchTeamNameRef.current);
        setShowSwitchCategoryModal(false);
        setIsSwitchCategoryLoading(false);
        setShowLifelineSuccessModal(true);
      },
      onError: () => {
        setIsSwitchCategoryLoading(false);
      },
    });
  };

  // Handle Switch Category modal close
  const handleSwitchCategoryModalClose = () => {
    setShowSwitchCategoryModal(false);
    switchTeamIdRef.current = "";
    switchTeamNameRef.current = "";
    switchLifelineRef.current = null;
  };

  // Handle lifeline modal close
  const handleLifelineModalClose = () => {
    setShowLifelineModal(false);
    setSelectedLifeline(null);
    setSelectedLifelineTeamId("");
    setSelectedLifelineTeamName("");
  };

  // Handle lifeline success modal close
  const handleLifelineSuccessClose = () => {
    setShowLifelineSuccessModal(false);
    setUsedLifeline(null);
    setUsedLifelineTeamName("");
    setLifelineSuccessMessage("");
    ra7El7lalTeamIdRef.current = "";
    ra7El7lalTeamNameRef.current = "";
    ra7El7lalLifelineRef.current = null;
    nukeTeamIdRef.current = "";
    nukeTeamNameRef.current = "";
    nukeLifelineIdRef.current = "";
    spinStealTeamIdRef.current = "";
    spinStealTeamNameRef.current = "";
    spinStealLifelineRef.current = null;
    switchTeamIdRef.current = "";
    switchTeamNameRef.current = "";
    switchLifelineRef.current = null;
  };

  // Show loading state only on first load (keep previous UI during background refetches)
  // if (!gameData && !gameDetailsFromLayout?.hasResolved) {
  //   return (
  //     <div className="flex justify-center items-center h-full min-h-[50vh] bg-primary-ivory">
  //       <Loader />
  //     </div>
  //   );
  // }

  return (
    <>
      {/* h-[calc(100%-6.770vw)] */}
      <div
        className="wrapper flex-1 flex flex-col bg-no-repeat xl:bg-size-[100%_100%] bg-cover bg-bottom bg-primary-ivory"
      // style={{
      //   backgroundImage: `url(${GamePlayImg})`,
      //   backgroundColor: "#B9F2FD",
      // }}
      >
        <section className="w-full flex justify-center items-center relative flex-1 min-h-0">
          {/* <span className="top-curve-shape lg:h-[1.563vw] md:h-[30px] h-5 w-full absolute lg:-bottom-[0.208vw] -bottom-1 start-0 z-10 bg-white-500"></span> */}
          <div
            className="w-full h-full bg-cover bg-center flex justify-center items-center"
          // style={{ backgroundImage: `url(${MainHeroBanner})` }}
          >
            <BoardSlider data={gameData?.categories} turnTeamName={current_team_turn?.team_name} />
          </div>
        </section>
        {/* top-curve-shape 2xl:pb-[3.125vw] soham 2xl:py-[2.55vw] xl:py-[2vw] lg:py-[1.8rem] md:py-[1.5rem] py-[1rem] bg-white-500 dashboard-footer-shape bg-cover bg-no-repeat landscape:max-lg:py-[16px]*/}
        <section className="game-bottom-banner 2xl:py-[0.6vw] xl:py-2 py-1 landscape:max-lg:py-1 relative">
          <div className="w-full 2xl:max-w-[72vw] md:max-w-[72vw] mx-auto 2xl:px-[0.833vw] xl:px-6 px-2 relative z-20">
            <div className="flex flex-wrap justify-center 2xl:gap-[3vw] xl:gap-7 md:gap-2 gap-1.5">
              {teamData?.map((team: any) => (
                <div
                  key={team._id}
                  className="w-[calc(50%-0.3rem)] landscape:max-md:w-[calc(25%-0.6rem)] xs:w-[calc(33.33%-6px)] sm:w-[calc(25%-0.6rem)] xl:w-[calc(25%-1.5rem)] 2xl:w-[calc(25%-3vw)]"
                >
                  <GameLifeline
                    team={team}
                    score={team.total_points}
                    scoreEditable
                    lifelines={team.lifelines}
                    onLifelineClick={handleLifelineClick}
                    isCurrentTurn={current_team_turn?._id === team._id}
                    disableAllLifelines={gameData?.isLifelineUsedForCurrentRound}
                  />
                </div>
              ))}

              {/* {teamData?.map((team: any, index) => {
                  if (index > 3) return null
                  return (
                    <div
                    key={team._id}
                    className="w-[calc(50%-0.375rem)] xs:w-[calc(25%-0.375rem)] sm:w-[calc(25%-1rem)] lg:w-[calc(25%-1.125rem)] 2xl:w-[calc(25%-0.65vw)] overflow-hidden"
                  >
                    <GameLifeline
                      team={team}
                      score={team.total_points}
                      lifelines={team.lifelines}
                      onLifelineClick={handleLifelineClick}
                    />
                  </div>
                  )
                })} */}
            </div>
          </div>
        </section>

        {/* Lifeline Confirmation Modal */}
        <LifelineConfirmModal
          isOpen={showLifelineModal}
          lifeline={selectedLifeline}
          teamName={selectedLifelineTeamName}
          onConfirm={handleLifelineConfirm}
          onClose={handleLifelineModalClose}
          isLoading={isUsingLifeline}
        />

        {/* Lifeline Success Modal */}
        <LifelineSuccessModal
          isOpen={showLifelineSuccessModal}
          lifeline={usedLifeline}
          teamName={usedLifelineTeamName}
          onClose={handleLifelineSuccessClose}
          autoCloseDelay={3000}
          customMessage={lifelineSuccessMessage || undefined}
        />

        {/* Ra7 El 7lal Modal */}
        <Ra7El7lalModal
          isOpen={showRa7El7lalModal}
          lifeline={ra7El7lalLifelineRef.current}
          teamId={ra7El7lalTeamIdRef.current}
          teamName={ra7El7lalTeamNameRef.current}
          teams={teamData || []}
          onConfirm={handleRa7El7lalConfirm}
          onClose={handleRa7El7lalModalClose}
          isLoading={isRa7El7lalLoading}
        />

        {/* Spin Steal Modal */}
        <SpinStealModal
          isOpen={showSpinStealModal}
          lifeline={spinStealLifelineRef.current}
          teamId={spinStealTeamIdRef.current}
          teamName={spinStealTeamNameRef.current}
          teams={teamData || []}
          onConfirm={handleSpinStealConfirm}
          onClose={handleSpinStealModalClose}
          isLoading={isSpinStealLoading}
        />

        {/* Switch Category Modal */}
        <SwitchCategoryModal
          isOpen={showSwitchCategoryModal}
          lifeline={switchLifelineRef.current}
          teamId={switchTeamIdRef.current}
          teamName={switchTeamNameRef.current}
          categories={gameData?.categories || []}
          onConfirm={handleSwitchCategoryConfirm}
          onClose={handleSwitchCategoryModalClose}
          isLoading={isSwitchCategoryLoading}
        />

        {/* Nuke Team Selection Modal */}
        {showNukeTeamModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center lg:p-[1%] p-4 z-50">
            <div className="modal bg-white-500 2xl:rounded-[2.083vw] lg:rounded-[40px] md:rounded-4xl rounded-3xl 2xl:p-[2vw] xl:p-10 md:p-7 p-4 2xl:max-w-[28vw] max-w-[540px] 2xl:shadow-[0px_0px_3.854vw_0px_#C3DC78CC_inset] shadow-[0px_0px_74px_0px_#C3DC78CC_inset] w-full relative animate-[slideUp_0.3s_ease-out] lg:border-[0.2vw] border-3 border-primary overflow-auto lg:max-h-[calc(100dvh-11%)] max-h-[calc(100dvh-40px)]">
              <div className="flex lg:gap-[1vw] gap-2 items-start justify-between w-full 2xl:mb-[1vw] md:mb-4 mb-3">
                <h1 className="2xl:text-[1.563vw]/[1.7vw] xl:text-3xl/6 text-2xl/5 font-semibold text-primary-orange">
                  {t("nuke.launchNuke")}
                </h1>
                <button
                  onClick={handleNukeTeamModalClose}
                  className="lg:w-[1.2vw] lg:h-[1.2vw] md:w-5 w-4 md:h-5 h-4 flex items-center justify-center cursor-pointer ms-auto hover:scale-110 transition-transform duration-300"
                >
                  <svg className="w-full h-full" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M15.9474 12.1123C15.8856 12.0504 15.8856 11.9496 15.9474 11.8878L23.5029 4.3286C24.149 3.68231 24.149 2.63063 23.5029 1.98429L22.0051 0.48573C21.692 0.172468 21.2758 0 20.833 0C20.3902 0 19.9739 0.172468 19.6609 0.485683L12.1054 8.04493C12.0672 8.08316 12.0235 8.09122 11.9937 8.09122C11.9639 8.09122 11.9203 8.08321 11.8821 8.04493L4.32658 0.485683C4.01351 0.172468 3.59726 0 3.15445 0C2.71164 0 2.29544 0.172468 1.98236 0.485683L0.484547 1.98429C-0.161516 2.63063 -0.161516 3.68231 0.484547 4.3286L8.04001 11.8878C8.10185 11.9496 8.10185 12.0504 8.04001 12.1123L0.484547 19.6714C-0.161469 20.3178 -0.161469 21.3694 0.484547 22.0157L1.98236 23.5143C2.29544 23.8275 2.71168 24 3.15445 24C3.59721 24 4.01351 23.8275 4.32658 23.5143L11.882 15.9551C11.9203 15.9169 11.9638 15.9089 11.9937 15.9089C12.0235 15.9089 12.0671 15.9169 12.1053 15.9552L19.6608 23.5143C19.9739 23.8276 20.3901 24 20.8329 24C21.2758 24 21.692 23.8276 22.005 23.5143L23.5028 22.0158C24.1489 21.3695 24.1489 20.3178 23.5028 19.6715L15.9474 12.1123Z"
                      fill="#333938"
                    />
                  </svg>
                </button>
              </div>

              <div className="w-full 2xl:mb-[1.042vw] mb-5">
                <svg className="w-full" width="540" height="1" viewBox="0 0 540 1" fill="none">
                  <line
                    y1="-0.5"
                    x2="540"
                    y2="-0.5"
                    transform="matrix(-1 0 0 1 540 1)"
                    stroke="url(#paint0_linear_nuke_board)"
                  />
                  <defs>
                    <linearGradient
                      id="paint0_linear_nuke_board"
                      x1="0"
                      y1="0.5"
                      x2="540"
                      y2="0.5"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#0B3369" stopOpacity="0" />
                      <stop offset="0.5" stopColor="#7364FF" />
                      <stop offset="1" stopColor="#0B3369" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                </svg>
              </div>

              {/* Nuke Info */}
              <div className="text-center 2xl:mb-[1vw] mb-4">
                <p className="2xl:text-[0.85vw] text-sm text-gray-600 mb-2">
                  <span className="font-semibold text-primary-orange">{nukeTeamNameRef.current}</span>{" "}
                  {t("nuke.isLaunchingNuke")}
                </p>
                <p className="2xl:text-[0.8vw] text-sm text-gray-700">{t("nuke.selectWhichTeamToTarget")}</p>
              </div>

              {/* Opponent Team Selection */}
              <div className="2xl:space-y-[0.833vw] md:space-y-4 space-y-3">
                <p className="2xl:text-[1vw] text-base font-semibold text-black-300 text-center">
                  {t("nuke.selectTargetTeam")}
                </p>
                {teamData
                  ?.filter((team: any) => team._id !== nukeTeamIdRef.current)
                  .map((team: any) => (
                    <button
                      key={team._id}
                      onClick={() => handleNukeTeamSelect(team._id, team.team_name)}
                      className="cursor-pointer w-full flex items-center justify-center 2xl:gap-[0.417vw] gap-2 2xl:py-[0.8vw] py-3 2xl:px-[1vw] px-4 rounded-full transition-all duration-300 bg-purple-fill 2xl:border-[0.26vw] border-5 border-transparent [background:padding-box_linear-gradient(#C0C0C0,#E9E9E9),border-box_linear-gradient(#E9E9E9,#B3B5B4)] hover:[background:padding-box_linear-gradient(#EF4444,#DC2626),border-box_linear-gradient(#DC2626,#EF4444)] hover:text-white text-black-300 2xl:text-[1vw] text-lg font-medium"
                    >
                      {team.team_name}
                    </button>
                  ))}
              </div>
            </div>
            <style>{`
              @keyframes slideUp {
                from { opacity: 0; transform: translateY(20px); }
                to { opacity: 1; transform: translateY(0); }
              }
            `}</style>
          </div>
        )}
      </div>
    </>
  );
};
export default GameBoard;
